import { Checkbox, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@mlm/ui';
import { type Branch, type BranchTypeFilter } from '../lib/api';
import {
  useBranchOptions,
  useBranches,
  useConfinedBranchIds,
  useIsBranchScoped,
  useScopedBranchLabel,
  type BranchSelection,
} from '../lib/branch-scope';

// Radix's Select can't hold an empty string as a value, so "no filter" rides a
// sentinel that is mapped back to undefined at the component boundary.
const ALL = '__all';

const label = (b: Branch) => `${b.name} (${b.code})`;

/** Single-select branch filter — the Reports filter row and the dashboard bar. */
export function BranchPicker({
  value,
  onChange,
  label: fieldLabel = 'Branch',
  allLabel = 'All branches',
  scopedFallback = 'hidden',
  filterType,
}: {
  value: string | undefined;
  onChange: (branchId: string | undefined) => void;
  label?: string;
  allLabel?: string;
  /**
   * Narrow the offered list to one `branches.type` ('branch' | 'stockist').
   * Paired with `BranchTypePicker` so the two controls can't contradict each
   * other — without it you could select a plain branch while filtering to
   * stockists and get an empty report with no visible cause. Client-side only:
   * `Branch.type` already rides the `GET /branches` payload.
   */
  filterType?: string;
  /**
   * What a branch-scoped operator sees. 'hidden' (default) renders nothing —
   * the long-standing Reports behaviour. 'label' renders a static, read-only
   * badge naming their branch: on the dashboard the numbers are already pinned
   * and several cards carry an "All branches" marker, so showing no cause at
   * all is more confusing than showing an uneditable one.
   */
  scopedFallback?: 'hidden' | 'label';
}) {
  const { data: branches } = useBranches();
  const confined = useConfinedBranchIds();
  const scopedLabel = useScopedBranchLabel();

  // Exactly one branch: there is nothing to pick, so keep the long-standing
  // read-only treatment.
  if (confined.length === 1) {
    if (scopedFallback === 'hidden') return null;
    return (
      <div>
        <p className="text-xs text-muted-foreground mb-1">{fieldLabel}</p>
        <div className="flex h-10 items-center rounded-md border border-dashed bg-muted/40 px-3 text-sm text-muted-foreground">
          {scopedLabel ?? 'Your branch'}
        </div>
      </div>
    );
  }

  // Several branches: a REAL picker, limited to theirs. The sentinel means
  // "all MY branches", not "all branches" — the label must not overstate what
  // they can see. Sending no branchId returns their whole permitted set, which
  // is exactly the auto-filtered default the feature asks for.
  const options = (branches ?? [])
    .filter((b) => !filterType || b.type === filterType)
    .filter((b) => confined.length === 0 || confined.includes(b.id));

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1">{fieldLabel}</p>
      <Select value={value ?? ALL} onValueChange={(v) => onChange(v === ALL ? undefined : v)}>
        <SelectTrigger className="h-10 min-w-[190px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{confined.length > 0 ? 'All my branches' : allLabel}</SelectItem>
          {options.map((b) => (
            <SelectItem key={b.id} value={b.id}>
              {label(b)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Branch TYPE filter. A stockist is not a separate entity — it's a branch row
 * with `branches.type = 'stockist'` — so this narrows the same dimension as
 * `BranchPicker` and sits beside it. Today only Stock on Hand uses it.
 *
 * Hidden for a branch-scoped operator: their one branch already has exactly one
 * type, so the control could only ever blank out their own report.
 */
export function BranchTypePicker({
  value,
  onChange,
}: {
  value: BranchTypeFilter | undefined;
  onChange: (v: BranchTypeFilter | undefined) => void;
}) {
  const scoped = useIsBranchScoped();
  if (scoped) return null;
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1">Type</p>
      <Select
        value={value ?? ALL}
        onValueChange={(v) => onChange(v === ALL ? undefined : (v as BranchTypeFilter))}
      >
        <SelectTrigger className="h-10 min-w-[160px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>All types</SelectItem>
          <SelectItem value="branch">Branches only</SelectItem>
          <SelectItem value="stockist">Stockists only</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Multi-select branch filter — the Reports filter row.
 *
 * A near-identical component lived here once, for the dashboard's
 * Sales-per-Branch card, and was removed when the dashboard grew a single
 * page-level branch filter: two branch controls on one page contradict each
 * other. That objection is specific to the dashboard and does NOT apply to
 * Reports, which has exactly one filter row. The dashboard keeps its single
 * `BranchPicker`; do not wire this one in there.
 *
 * Nothing is ticked to begin with and the report waits — a report that silently
 * runs tenant-wide before anyone chose a branch reads as "these are my branch's
 * numbers" to whoever is looking at it. See `BranchSelection` for the tri-state.
 *
 * Unlike the version that was deleted, this one is scope-aware — the tick boxes
 * are the operator's OWN branches when they're confined to several.
 */
export function BranchCheckboxList({
  selected,
  onChange,
  label: fieldLabel = 'Branches',
  filterType,
}: {
  selected: BranchSelection;
  onChange: (next: BranchSelection) => void;
  label?: string;
  /** Same contract as `BranchPicker.filterType` — keeps the two controls honest. */
  filterType?: string;
}) {
  const { options, confined, isPending, hidden } = useBranchOptions(filterType);
  if (isPending || hidden) return null;

  const allOn = selected === null;
  const picked = selected ?? [];
  const toggle = (id: string) => {
    // Ticking a box off "All" selects THAT branch alone, which is what an
    // unticked box being clicked should do. (The old behaviour materialized
    // all-but-this-one, which only made sense while every box rendered ticked.)
    if (allOn) {
      onChange([id]);
      return;
    }
    // Never collapse a full selection to `null` — see `BranchSelection`: the two
    // differ on branch-less orders, so that would silently change the result set.
    onChange(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);
  };

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1">{fieldLabel}</p>
      <div className="flex min-h-10 flex-wrap items-center gap-x-4 gap-y-1.5 rounded-md border px-3 py-2">
        <label className="flex cursor-pointer items-center gap-1.5 text-xs">
          <Checkbox checked={allOn} onCheckedChange={() => onChange(allOn ? [] : null)} />
          <span className={allOn ? 'font-medium' : 'text-muted-foreground'}>
            {confined.length > 0 ? 'All mine' : 'All'}
          </span>
        </label>
        {options.map((b) => {
          const on = !allOn && picked.includes(b.id);
          return (
            <label
              key={b.id}
              title={label(b)}
              className="flex cursor-pointer items-center gap-1.5 text-xs"
            >
              <Checkbox checked={on} onCheckedChange={() => toggle(b.id)} />
              <span className={on ? '' : 'text-muted-foreground'}>{b.code}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
